// 진로 전문 분석 엔진
// Holland(RIASEC) 흥미유형 + 직업 가치관을 결합해
// 전문 상담 리포트 수준의 상세 분석을 생성한다.

export interface CareerProfileInput {
  hollandScores: Record<string, number>;
  valueScores: Record<string, number>;
  topHolland: string[]; // 상위 2개 코드
  topValues: string[]; // 상위 2개 가치
  hollandMax: number;
  valueMax: number;
}

export interface CompetencyItem {
  name: string;
  desc: string;
}

export interface RoadmapPhase {
  phase: string;
  focus: string;
  activities: string[];
}

export interface CareerReportData {
  hollandCode: string; // 예: "RI"
  codeTitle: string; // 조합 유형 이름
  codeSummary: string; // 조합 해석 한 문단
  congruence: {
    score: number; // 0~100 흥미-가치관 정합도
    level: string; // 매우 높음/높음/보통/낮음
    comment: string;
  };
  consistency: {
    score: number; // 0~100 흥미 프로파일 일관성
    level: string;
    comment: string;
  };
  differentiation: {
    score: number; // 0~100 변별도(뚜렷함)
    level: string;
    comment: string;
  };
  strengths: CompetencyItem[];
  developments: CompetencyItem[];
  learningStrategy: string[];
  roadmap: RoadmapPhase[];
  activities: string[];
  certificates: string[];
  hobbies: string[];
  talents: string[];
  cautions: string[];
}

const HOLLAND_LABEL: Record<string, string> = {
  R: '현실형', I: '탐구형', A: '예술형', S: '사회형', E: '진취형', C: '관습형',
};

// RIASEC 인접성(육각형 모형) — 인접할수록 일관성 높음
const HEXAGON_ORDER = ['R', 'I', 'A', 'S', 'E', 'C'];

function hexDistance(a: string, b: string): number {
  const ia = HEXAGON_ORDER.indexOf(a);
  const ib = HEXAGON_ORDER.indexOf(b);
  if (ia < 0 || ib < 0) return 3;
  const raw = Math.abs(ia - ib);
  return Math.min(raw, HEXAGON_ORDER.length - raw); // 0~3
}

// 2코드 조합 해석
const CODE_INTERPRETATION: Record<string, { title: string; summary: string }> = {
  RI: { title: '기술·과학 탐구형', summary: '손으로 직접 다루는 실용적 작업과 원리를 파고드는 탐구 성향이 결합되어, 실험·설계·개발처럼 이론을 실제로 구현하는 활동에서 강점을 발휘합니다.' },
  RA: { title: '실용·창작형', summary: '구체적인 제작 능력과 예술적 감각이 어우러져, 제품 디자인·건축·공예처럼 기능과 미(美)를 동시에 다루는 분야에 잘 맞습니다.' },
  RS: { title: '실무·조력형', summary: '실질적인 기술과 사람을 돕는 성향이 결합되어, 현장에서 사람을 직접 지원하는 기술 서비스·보건·안전 분야에 적합합니다.' },
  RE: { title: '기술·경영형', summary: '실무 역량과 주도적 추진력이 결합되어, 현장을 관리하고 사업을 이끄는 기술 경영·창업 분야에서 두각을 나타냅니다.' },
  RC: { title: '기술·관리형', summary: '실용적 작업과 체계적 정리 성향이 결합되어, 정밀·품질·설비 관리처럼 정확성과 기술이 함께 필요한 분야에 강합니다.' },
  IR: { title: '과학·기술 탐구형', summary: '깊이 있는 탐구력과 실용적 구현력이 결합되어, 공학 연구·데이터 분석·응용과학 분야에서 잠재력이 큽니다.' },
  IA: { title: '탐구·창의형', summary: '분석적 사고와 독창적 상상력이 결합되어, 기초과학·연구개발·이론 설계처럼 새로운 지식을 창출하는 분야에 적합합니다.' },
  IS: { title: '탐구·조력형', summary: '지적 탐구심과 사람을 돕는 마음이 결합되어, 의학·심리·교육연구처럼 지식으로 사람을 이롭게 하는 분야에 잘 맞습니다.' },
  IE: { title: '탐구·리더형', summary: '분석력과 추진력이 결합되어, 전략 컨설팅·연구 기획·기술 사업화처럼 지식을 실행으로 옮기는 분야에서 강점을 보입니다.' },
  IC: { title: '분석·관리형', summary: '탐구력과 체계성이 결합되어, 통계·회계·데이터 관리처럼 정밀한 분석과 정리가 필요한 분야에 적합합니다.' },
  AI: { title: '창의·탐구형', summary: '예술적 감각과 지적 호기심이 결합되어, 콘텐츠 기획·미디어·융합 연구처럼 창의와 논리가 만나는 분야에 강합니다.' },
  AR: { title: '창작·실용형', summary: '독창적 표현력과 제작 능력이 결합되어, 디자인·영상·공예처럼 아이디어를 실제 작품으로 구현하는 분야에 적합합니다.' },
  AS: { title: '예술·소통형', summary: '창의적 표현력과 공감 능력이 결합되어, 문화예술 교육·상담·방송처럼 사람과 감성으로 소통하는 분야에 잘 맞습니다.' },
  AE: { title: '창작·기획형', summary: '예술적 감각과 주도적 기획력이 결합되어, 광고·브랜딩·콘텐츠 사업처럼 창의를 사업으로 이끄는 분야에서 두각을 보입니다.' },
  AC: { title: '표현·정리형', summary: '창의성과 체계성이 결합되어, 편집·아카이빙·전시 기획처럼 감각과 정돈이 함께 필요한 분야에 적합합니다.' },
  SA: { title: '조력·창의형', summary: '사람을 돕는 마음과 예술적 표현력이 결합되어, 교육·문화복지·예술치료처럼 감성으로 사람을 지원하는 분야에 강합니다.' },
  SI: { title: '조력·탐구형', summary: '공감 능력과 지적 탐구심이 결합되어, 상담·사회연구·교육개발처럼 사람을 깊이 이해하고 돕는 분야에 잘 맞습니다.' },
  SR: { title: '조력·실무형', summary: '사람을 돕는 성향과 실질적 기술이 결합되어, 보건·재활·현장 지원처럼 직접적인 도움을 주는 분야에 적합합니다.' },
  SE: { title: '사회·리더형', summary: '공감 능력과 주도성이 결합되어, 교육행정·사회사업 기획·공공 리더십처럼 사람을 이끌고 조직하는 분야에서 강점을 보입니다.' },
  SC: { title: '조력·관리형', summary: '사람을 돕는 마음과 꼼꼼함이 결합되어, 상담행정·복지관리·교육운영처럼 지원과 체계가 함께 필요한 분야에 적합합니다.' },
  ES: { title: '리더·사회형', summary: '추진력과 공감 능력이 결합되어, 경영·인사·공공정책처럼 사람을 이끌며 조직을 성장시키는 분야에 잘 맞습니다.' },
  EI: { title: '진취·탐구형', summary: '주도성과 분석력이 결합되어, 전략·마케팅·사업 기획처럼 통찰을 바탕으로 사업을 이끄는 분야에 적합합니다.' },
  EA: { title: '기획·창의형', summary: '추진력과 창의성이 결합되어, 콘텐츠 사업·광고·문화기획처럼 아이디어를 사업으로 확장하는 분야에서 두각을 보입니다.' },
  ER: { title: '경영·실무형', summary: '리더십과 실무 역량이 결합되어, 창업·현장 경영·기술 영업처럼 실질적 성과를 이끄는 분야에 강합니다.' },
  EC: { title: '경영·관리형', summary: '추진력과 체계성이 결합되어, 재무·경영관리·프로젝트 관리처럼 조직을 계획적으로 운영하는 분야에 적합합니다.' },
  CR: { title: '관리·실무형', summary: '체계성과 실무 능력이 결합되어, 품질·회계·설비관리처럼 정확성과 실행이 함께 필요한 분야에 잘 맞습니다.' },
  CI: { title: '관리·분석형', summary: '꼼꼼함과 분석력이 결합되어, 통계·감사·데이터 관리처럼 정밀한 검증이 중요한 분야에 강점을 보입니다.' },
  CE: { title: '관리·경영형', summary: '체계성과 추진력이 결합되어, 재무관리·행정·경영지원처럼 조직을 안정적으로 운영하는 분야에 적합합니다.' },
  CS: { title: '관리·조력형', summary: '꼼꼼함과 사람을 돕는 성향이 결합되어, 행정·인사·고객관리처럼 사람을 체계적으로 지원하는 분야에 잘 맞습니다.' },
  CA: { title: '정리·창의형', summary: '체계성과 창의성이 결합되어, 편집·기록관리·전시 운영처럼 정돈과 감각이 함께 필요한 분야에 적합합니다.' },
};

// 유형별 핵심 역량
const STRENGTH_MAP: Record<string, CompetencyItem[]> = {
  R: [{ name: '실행력', desc: '아이디어를 직접 손으로 구현하고 문제를 실질적으로 해결합니다.' }, { name: '도구·기술 활용', desc: '장비와 도구를 능숙하게 다루며 몸으로 익히는 학습에 강합니다.' }],
  I: [{ name: '분석적 사고', desc: '복잡한 현상을 논리적으로 쪼개어 원인을 규명합니다.' }, { name: '탐구·연구력', desc: '궁금한 것을 끝까지 파고들어 깊이 있게 이해합니다.' }],
  A: [{ name: '창의·발상', desc: '기존 틀을 넘어 독창적인 아이디어를 만들어냅니다.' }, { name: '표현력', desc: '생각과 감정을 다양한 매체로 풍부하게 표현합니다.' }],
  S: [{ name: '공감·소통', desc: '상대의 감정을 이해하고 신뢰를 바탕으로 소통합니다.' }, { name: '협력·조력', desc: '함께 일하며 다른 사람의 성장을 돕습니다.' }],
  E: [{ name: '리더십·추진력', desc: '목표를 세우고 사람과 자원을 이끌어 성과를 냅니다.' }, { name: '설득력', desc: '자신의 비전으로 사람들을 움직이고 협상합니다.' }],
  C: [{ name: '체계성·정확성', desc: '자료를 정리하고 규칙에 따라 정밀하게 처리합니다.' }, { name: '계획·관리', desc: '일의 순서를 설계하고 꼼꼼하게 관리합니다.' }],
};

// 유형별 보완이 필요한 개발 영역(대각/반대 유형 성격)
const DEVELOP_MAP: Record<string, CompetencyItem> = {
  R: { name: '표현·소통', desc: '생각을 말과 글로 정리해 전달하는 연습이 도움이 됩니다.' },
  I: { name: '협업·실행', desc: '분석에 머무르지 않고 팀과 함께 실행으로 옮기는 경험이 필요합니다.' },
  A: { name: '체계·마감', desc: '자유로운 발상을 구체적 계획과 마감으로 완성하는 습관이 필요합니다.' },
  S: { name: '논리·데이터', desc: '감정과 함께 객관적 근거·데이터로 판단하는 훈련이 도움이 됩니다.' },
  E: { name: '경청·분석', desc: '앞서 나가기 전에 깊이 듣고 분석하는 균형이 필요합니다.' },
  C: { name: '유연성·창의', desc: '정해진 틀 밖의 새로운 시도를 받아들이는 유연함이 도움이 됩니다.' },
};

const LEARNING_MAP: Record<string, string> = {
  R: '개념을 실험·실습·제작 활동과 연결해 몸으로 익히면 이해가 빨라집니다.',
  I: '왜 그런지 원리를 스스로 질문하고 자료를 찾아 정리하는 탐구식 학습이 효과적입니다.',
  A: '마인드맵·시각 자료·스토리로 재구성하며 자신만의 방식으로 표현하면 오래 기억됩니다.',
  S: '친구에게 설명하거나 스터디로 함께 토론하며 배우면 몰입도가 높아집니다.',
  E: '학습 목표를 프로젝트처럼 설정하고 도전 과제로 만들면 동기가 강해집니다.',
  C: '체계적인 계획표와 오답노트로 단계별로 정리하며 반복하면 안정적으로 성장합니다.',
};

// 가치관별 방향 코멘트
const VALUE_DIRECTION: Record<string, string> = {
  achievement: '성취 지향적이므로 명확한 목표와 도전 과제가 있는 진로에서 만족도가 높습니다.',
  helping: '사회공헌을 중시하므로 사람과 사회에 기여하는 진로에서 보람을 크게 느낍니다.',
  creativity: '창의·표현을 중시하므로 자율성과 창작 기회가 있는 진로가 잘 맞습니다.',
  intellectual: '지적 탐구를 중시하므로 지속적으로 배우고 연구하는 진로에서 몰입합니다.',
  leadership: '영향력을 중시하므로 조직을 이끌고 변화를 만드는 진로에서 동기가 강합니다.',
  stability: '안정·전문성을 중시하므로 한 분야에서 깊이 성장하는 진로에서 안정감을 느낍니다.',
};

// 가치관 한글 라벨 (화면 표시용)
const VALUE_LABEL: Record<string, string> = {
  achievement: '성취감',
  helping: '사회공헌',
  creativity: '창의·표현',
  intellectual: '지적 탐구',
  leadership: '영향력',
  stability: '안정·전문성',
};

// 흥미유형 × 가치관 정합 매트릭스(0~1)
const CONGRUENCE_MATRIX: Record<string, Record<string, number>> = {
  R: { achievement: 0.9, stability: 0.9, helping: 0.4, creativity: 0.6, intellectual: 0.7, leadership: 0.6 },
  I: { intellectual: 1.0, achievement: 0.8, creativity: 0.7, stability: 0.7, helping: 0.6, leadership: 0.6 },
  A: { creativity: 1.0, intellectual: 0.7, achievement: 0.6, helping: 0.6, leadership: 0.6, stability: 0.4 },
  S: { helping: 1.0, stability: 0.7, achievement: 0.6, creativity: 0.6, leadership: 0.7, intellectual: 0.6 },
  E: { leadership: 1.0, achievement: 0.9, stability: 0.6, helping: 0.6, creativity: 0.7, intellectual: 0.6 },
  C: { stability: 1.0, achievement: 0.7, helping: 0.6, intellectual: 0.7, leadership: 0.7, creativity: 0.4 },
};

const ACTIVITY_MAP: Record<string, string[]> = {
  R: ['메이커·발명 동아리', '로봇·코딩 대회 참가', '과학 실험/공학 캠프'],
  I: ['과학 탐구 R&E 프로젝트', '수학·과학 올림피아드', '주제 탐구 논문 작성'],
  A: ['디자인·영상 제작 동아리', '교내 예술제·공모전 출품', '창작 포트폴리오 제작'],
  S: ['또래 멘토링·봉사 동아리', '학급 임원·상담 도우미', '지역 사회 봉사 활동'],
  E: ['학생회·창업 동아리', '토론·모의유엔 대회', '프로젝트 팀 리더 경험'],
  C: ['통계·데이터 분석 동아리', '경제·회계 관련 자격 준비', '학습 계획·기록 관리 프로젝트'],
};

const CERT_MAP: Record<string, string[]> = {
  R: ['정보처리기능사', '전자기기기능사', 'GTQ 그래픽자격'],
  I: ['정보처리기능사', '데이터분석 준전문가(ADsP)', '코딩 관련 인증'],
  A: ['GTQ 그래픽자격', '컴퓨터그래픽스운용기능사', '영상편집 관련 자격'],
  S: ['한국어능력시험', '심리상담 관련 민간자격', '수화·봉사 관련 인증'],
  E: ['무역영어', '경영·마케팅 관련 자격', '스피치·발표 인증'],
  C: ['컴퓨터활용능력', '전산회계·회계관리', 'ADsP 데이터분석 준전문가'],
};

// 유형별 어울리는 취미
const HOBBY_MAP: Record<string, string[]> = {
  R: ['모형·프라모델 제작', '드론·RC 조립', 'DIY 목공·인테리어', '등산·캠핑'],
  I: ['과학 실험', '독서·지식 탐구', '퍼즐·보드게임', '천체 관측'],
  A: ['그림·일러스트 그리기', '악기 연주', '사진 촬영', '글쓰기·웹툰 창작'],
  S: ['봉사 활동', '스터디 리드', '단체 스포츠', '멘토링'],
  E: ['토론·발표 동아리', '구기 스포츠', '창업·프로젝트', '여행 기획'],
  C: ['컬렉션 정리', '가계부·데이터 기록', '퍼즐·스도쿠', '다이어리 꾸미기'],
};

// 유형별 어울리는 특기·재능
const TALENT_MAP: Record<string, string[]> = {
  R: ['손재주', '기계·도구 다루기', '공간 지각력', '체력·운동 감각'],
  I: ['논리적 사고', '문제 해결력', '분석·추론 능력', '집중력'],
  A: ['창의력', '표현력', '미적 감각', '감수성'],
  S: ['공감 능력', '소통·경청', '협업', '배려형 리더십'],
  E: ['리더십', '설득·협상', '기획력', '추진력'],
  C: ['정리정돈', '정확성', '계획·관리', '꼼꼼함'],
};

function levelFromScore(score: number): string {
  if (score >= 80) return '매우 높음';
  if (score >= 60) return '높음';
  if (score >= 40) return '보통';
  return '낮음';
}

export function buildCareerReport(input: CareerProfileInput): CareerReportData {
  const [c1, c2] = input.topHolland;
  const code = `${c1 ?? ''}${c2 ?? ''}`;
  const interp = CODE_INTERPRETATION[code] || {
    title: `${HOLLAND_LABEL[c1] ?? ''} 중심형`,
    summary: `${HOLLAND_LABEL[c1] ?? ''} 성향이 두드러지며, 이를 살릴 수 있는 진로에서 강점을 발휘합니다.`,
  };

  // 일관성(consistency): 상위 2코드가 육각형에서 가까울수록 높음
  const dist = hexDistance(c1, c2);
  const consistencyScore = Math.round(((3 - dist) / 3) * 100);
  const consistencyComment =
    dist <= 1
      ? '상위 두 흥미유형이 서로 잘 어울려, 진로 방향이 뚜렷하고 안정적으로 나타납니다.'
      : dist === 2
        ? '상위 두 흥미유형이 어느 정도 조화를 이루어, 폭넓은 진로 선택이 가능합니다.'
        : '서로 성격이 다른 흥미유형이 함께 나타나, 다양한 가능성을 탐색해보는 것이 좋습니다.';

  // 변별도(differentiation): 최고-최저 흥미 점수 차이
  const hVals = Object.values(input.hollandScores);
  const hMaxV = Math.max(...hVals, 0);
  const hMinV = Math.min(...hVals, 0);
  const diffScore = input.hollandMax > 0 ? Math.round(((hMaxV - hMinV) / input.hollandMax) * 100) : 0;
  const diffComment =
    diffScore >= 50
      ? '흥미의 강약이 분명해 자신이 좋아하는 활동이 확실합니다.'
      : diffScore >= 25
        ? '흥미가 비교적 고르게 나타나 여러 분야에 열려 있습니다.'
        : '흥미가 매우 고르게 분포해, 다양한 경험을 통해 관심을 좁혀가는 것이 좋습니다.';

  // 정합도(congruence): 상위 흥미 × 상위 가치
  const v1 = input.topValues[0];
  const congBase = (CONGRUENCE_MATRIX[c1]?.[v1] ?? 0.6);
  const congSecond = c2 && input.topValues[1] ? (CONGRUENCE_MATRIX[c2]?.[input.topValues[1]] ?? 0.6) : congBase;
  const congruenceScore = Math.round(((congBase * 0.7) + (congSecond * 0.3)) * 100);
  const congruenceComment = `${HOLLAND_LABEL[c1] ?? ''} 흥미와 '${VALUE_LABEL[v1] ?? ''}' 가치관을 종합하면, ${VALUE_DIRECTION[v1] ?? '자신에게 맞는 방향을 찾아가는 것이 중요합니다.'}`;

  const strengths = [
    ...(STRENGTH_MAP[c1] ?? []),
    ...((c2 ? STRENGTH_MAP[c2]?.slice(0, 1) : []) ?? []),
  ];

  const developments = [DEVELOP_MAP[c1], c2 ? DEVELOP_MAP[c2] : null].filter(Boolean) as CompetencyItem[];

  const learningStrategy = [LEARNING_MAP[c1], c2 ? LEARNING_MAP[c2] : ''].filter(Boolean);

  const activities = Array.from(new Set([...(ACTIVITY_MAP[c1] ?? []), ...((c2 ? ACTIVITY_MAP[c2] : []) ?? [])])).slice(0, 5);
  const certificates = Array.from(new Set([...(CERT_MAP[c1] ?? []), ...((c2 ? CERT_MAP[c2] : []) ?? [])])).slice(0, 5);
  const hobbies = Array.from(new Set([...(HOBBY_MAP[c1] ?? []), ...((c2 ? HOBBY_MAP[c2] : []) ?? [])])).slice(0, 6);
  const talents = Array.from(new Set([...(TALENT_MAP[c1] ?? []), ...((c2 ? TALENT_MAP[c2] : []) ?? [])])).slice(0, 6);

  const roadmap: RoadmapPhase[] = [
    {
      phase: '중3 · 고1 (탐색기)',
      focus: '흥미 확인과 기초 다지기',
      activities: [
        `${interp.title} 관련 진로 체험·직업인 인터뷰 참여`,
        '관심 분야 동아리 가입 및 독서로 배경지식 넓히기',
        '국어·영어·수학 등 기초 과목의 학습 습관 정착',
      ],
    },
    {
      phase: '고2 (집중기)',
      focus: '진로 구체화와 선택과목 설계',
      activities: [
        '추천 계열에 맞는 일반선택·진로선택 과목 이수',
        `${activities[0] ?? '관심 분야 프로젝트'} 등 심화 활동으로 세특 채우기`,
        '희망 전공의 학과·교육과정 조사 및 탐구 보고서 작성',
      ],
    },
    {
      phase: '고3 (실행기)',
      focus: '전형 준비와 최종 결정',
      activities: [
        '희망 대학·학과의 전형 요소(교과/종합/논술) 분석',
        '자기소개·면접에서 활용할 진로 활동 스토리 정리',
        '수능·내신 목표 설정 및 지원 전략 수립',
      ],
    },
  ];

  const cautions = [
    '이 결과는 현재 시점의 흥미·가치관을 바탕으로 한 참고 자료이며, 경험이 쌓이면 변할 수 있습니다.',
    '한 가지 결과에 진로를 가두기보다, 추천 분야를 출발점으로 직접 체험하며 넓혀가세요.',
    consistencyScore < 40 ? '흥미가 다양하게 나타났으므로, 여러 분야를 직접 경험하며 관심을 좁혀가는 것이 특히 중요합니다.' : '추천 활동을 통해 흥미를 실제 경험으로 검증해보는 것을 권장합니다.',
  ];

  return {
    hollandCode: code,
    codeTitle: interp.title,
    codeSummary: interp.summary,
    congruence: { score: congruenceScore, level: levelFromScore(congruenceScore), comment: congruenceComment },
    consistency: { score: consistencyScore, level: levelFromScore(consistencyScore), comment: consistencyComment },
    differentiation: { score: diffScore, level: levelFromScore(diffScore), comment: diffComment },
    strengths,
    developments,
    learningStrategy,
    roadmap,
    activities,
    certificates,
    hobbies,
    talents,
    cautions,
  };
}